"use client";

import { useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { FormattedMessage } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";

type RootErrorProps = {
  /**
   * `digest` is Next's own field. In production the boundary is handed a redacted error,
   * so the digest is the only key back to the real message in the server log.
   */
  error: Error & { digest?: string };
  reset: () => void;
};

/**
 * Fallback error boundary for any route without its own `error.tsx`. Keep this generic —
 * route-specific boundaries (e.g. the home page's) should stay closer to their route instead
 * of being folded in here.
 */
const RootError = ({ error, reset }: RootErrorProps) => {
  useEffect(() => {
    // Send error to datadog. The digest travels as context,
    // without it a production report says only that the render failed.
    datadogRum.addError(error, { digest: error.digest });
  }, [error]);

  return (
    <main
      className="tw-flex tw-min-h-[calc(100vh-72px)] tw-flex-col tw-items-center tw-justify-center tw-gap-16 tw-px-16 tw-text-center"
      data-testing-id="root-error"
    >
      <Text
        element="h1"
        typeface="headline-4"
        data-testing-id="root-error-title"
      >
        <FormattedMessage defaultMessage="We couldn't load this page." />
      </Text>
      <Text element="p" typeface="body-1" data-testing-id="root-error-message">
        <FormattedMessage defaultMessage="Give it another try in a moment." />
      </Text>
      <Button data-testing-id="root-error-retry" onClick={reset}>
        <FormattedMessage defaultMessage="Try again" />
      </Button>
    </main>
  );
};

export default RootError;
